import { Button, Form, Col, Row, InputGroup, Container } from "react-bootstrap";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
    BackButton,
    Checkbox,
    ControlledDateInput,
    DateInput,
    DateTimeInputs,
    Icon,
    Input,
    Select,
    StatefulForm,
    useStatefulForm,
} from "../../../component-library";
import { useWatch } from "react-hook-form";
import { useAccountQuery, usePersonQuery, useSurgeryQuery, useTissueTypeQuery } from "../../../queries";
import { Case, QueryType, TimeZones, PersonTypes, SelectOption } from "../../../models";
import { getSurgeryName } from "../../../helper-functions/getSurgeryName";
import { useAccountSelectOptions, useAxios, useSelectOptionsFromSlug } from "../../../hooks";
import { useNavigate } from "react-router-dom";
import { getUtcDate } from "../../../helper-functions";
import ReactSelect from "react-select";
import { useToasts } from "../../../contexts";
import { fas } from "@fortawesome/free-solid-svg-icons";

const ImportTissueFormSchema = z.object({
    tissueNumber: z.string().min(1, "Required"),
    side: z.string().min(1, "Required"),
    tissueTypeId: z.number().min(1, "Required"),
    preservationDate: z.string().min(1, "Required"),
    preservationTime: z.string().min(1, "Required"),
    preservationTimeZone: z.string().min(1, "Required"),
    donorAge: z.number().min(0, "Required"),
    donorGender: z.string().min(1, "Required"),
    timeOfDeathDate: z.string().min(1, "Required"),
    timeOfDeathTime: z.string().min(1, "Required"),
    timeOfDeathTimeZone: z.string().min(1, "Required"),
    causeOfDeath: z.string().min(1, "Required"),
    storageMedium: z.string().min(1, "Required"),
    storageMediumOther: z.string().optional(),
    mediumLotNumber: z.string().min(1, "Required"),
    mediumExpirationDate: z.string().min(1, "Required"),
    sourceAccountId: z.number().min(1, "Required"),
    importAccountId: z.number().optional(),
    tissueFee: z.number().optional(),
    shippingFee: z.number().optional(),
    poNumber: z.string().optional(),
    isShippedDirect: z.boolean(),
    surgeryId: z.number().optional(),
    isProcessReturn: z.boolean(),
});

export type ImportTissueFormModel = z.infer<typeof ImportTissueFormSchema>;

export const ImportTissueForm = () => {
    const { errorToast } = useToasts();
    const navigate = useNavigate();
    const { post } = useAxios();
    const [surgeryDate, setSurgeryDate] = useState<string | undefined>();
    const [surgeonId, setSurgeonId] = useState<number>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const timeZoneOptions = useSelectOptionsFromSlug("time_zone");
    const central = timeZoneOptions.find((tz) => tz.value === TimeZones.Central);

    const defaultValues = useMemo((): Partial<ImportTissueFormModel> => {
        return {
            timeOfDeathTimeZone: central?.value,
            preservationTimeZone: central?.value,
        };
    }, [central?.value]);

    const { listQuery: tissueTypeQuery } = useTissueTypeQuery({ queryType: QueryType.List });

    const tissueTypeOptions =
        tissueTypeQuery.data?.map((tissueType) => ({
            label: tissueType.name,
            value: tissueType.id,
        })) ?? [];

    const { options: eyeBankOptions } = useAccountSelectOptions("Eye Bank", undefined, false);

    const { listQuery: internalAccountsQuery } = useAccountQuery({ queryType: QueryType.List, isInternal: true });

    const internalAccountOptions =
        internalAccountsQuery.data?.map((account) => ({
            label: account.name,
            value: account.id,
        })) ?? [];

    const { listQuery: surgeonQuery } = usePersonQuery({
        queryType: QueryType.List,
        personType: PersonTypes.DoctorSurgeon,
    });

    const surgeonOptions: SelectOption[] =
        surgeonQuery?.data?.map((s) => ({
            label: `${s.firstName} ${s.lastName}`,
            value: s.id,
        })) ?? [];

    const { listQuery: surgeryQuery } = useSurgeryQuery({
        queryType: surgeryDate ? QueryType.List : undefined,
        surgeonId: surgeonId ?? undefined,
        timeLow: surgeryDate,
        timeHigh: surgeryDate,
    });

    const surgeryOptions =
        surgeryQuery.data
            ?.filter((surgery) => !surgery.tissueId)
            .map((surgery) => ({
                label: getSurgeryName(surgery),
                value: surgery.id,
            })) ?? [];

    const { methods, isDirty } = useStatefulForm({
        suppressReset: true,
        defaultValues,
        schema: ImportTissueFormSchema,
    });
    const { control, reset } = methods;

    const formValues = useWatch({ control });

    const tissueExpirationDate = useMemo(() => {
        if (!formValues.preservationDate) return "";

        const preservationDateUtc = getUtcDate(
            formValues.preservationDate,
            formValues.preservationTime,
            formValues.preservationTimeZone
        );
        if (!preservationDateUtc) return "";

        const preservationDate = new Date(preservationDateUtc);
        preservationDate.setDate(preservationDate.getDate() + 14);
        return preservationDate.toString();
    }, [formValues.preservationDate, formValues.preservationTime, formValues.preservationTimeZone]);

    const total = useMemo(() => {
        return (Number(formValues.tissueFee ?? 0) + Number(formValues.shippingFee ?? 0)).toFixed(2);
    }, [formValues.tissueFee, formValues.shippingFee]);

    const handleSubmit = async (values: ImportTissueFormModel) => {
        setIsSubmitting(true);
        try {
            const timeOfDeathUtc = getUtcDate(
                values.timeOfDeathDate,
                values.timeOfDeathTime,
                values.timeOfDeathTimeZone
            );

            const response = await post<any, Case>({
                url: "/cases/import-tissue",
                data: { ...values, tissueNumber: values.tissueNumber.trim(), timeOfDeathUtc },
            });

            reset();
            if (response.tissues?.length) {
                navigate(`/lists/none/tissues/${response.tissues[0].id}/placement`);
            } else {
                navigate(-1);
            }
        } catch (error) {
            errorToast(error);
        }
        setIsSubmitting(false);
    };

    return (
        <Container>
            <h1>Import Tissue</h1>
            <BackButton />
            <StatefulForm
                onSubmit={(values) => handleSubmit(values as ImportTissueFormModel)}
                methods={methods}
                noSubmitOnEnter
            >
                <Row>
                    <Col sm={2}>
                        <Checkbox
                            accessor="isProcessReturn"
                            label={
                                <>
                                    Process/Return
                                    <Icon
                                        icon={fas.faInfoCircle}
                                        className="text-primary ms-1"
                                        title="Schedule the procedure after the tissue record is created"
                                        aria-label="Schedule the procedure after the tissue record is created"
                                    />
                                </>
                            }
                        />
                    </Col>
                    <Col sm={4}>
                        <Input accessor="tissueNumber" label="Tissue Number" autoFocus required />
                    </Col>
                    <Col sm={2}>
                        <Select
                            accessor="side"
                            label="OD/OS"
                            options={[
                                { label: "OD", value: "OD" },
                                { label: "OS", value: "OS" },
                            ]}
                            required
                        />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Select accessor="tissueTypeId" label="Tissue Type" options={tissueTypeOptions} required />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Form.Label>
                            Date/Time of Preservation <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                            <DateTimeInputs
                                label="Date/Time of Preservation"
                                dateAccessor="preservationDate"
                                timeAccessor="preservationTime"
                                ignoreTimeZone
                                inGroup
                                required
                            />
                            <Select
                                accessor="preservationTimeZone"
                                placeholder="time zone"
                                slug="time_zone"
                                inGroup
                                required
                            />
                        </InputGroup>
                    </Col>
                    <Col sm={4} className="mb-3">
                        <ControlledDateInput
                            readOnly
                            label="Date of Tissue Expiration"
                            value={tissueExpirationDate}
                            onChange={() => {}}
                        />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Select accessor="sourceAccountId" label="Source Eye Bank" options={eyeBankOptions} required />
                    </Col>
                    <Col sm={2} className="mb-3">
                        <Input accessor="donorAge" label="Donor Age" type="number" min={0} required />
                    </Col>
                    <Col sm={2} className="mb-3">
                        <Select accessor="donorGender" label="Donor Gender" slug="gender" required />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Form.Label>
                            Date/Time of Death <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                            <DateTimeInputs
                                dateAccessor="timeOfDeathDate"
                                timeAccessor="timeOfDeathTime"
                                label="Date/Time of Death"
                                ignoreTimeZone
                                inGroup
                                required
                            />
                            <Select
                                accessor="timeOfDeathTimeZone"
                                placeholder="time zone"
                                slug="time_zone"
                                inGroup
                                required
                            />
                        </InputGroup>
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Input accessor="causeOfDeath" label="Cause of Death" required />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <Select accessor="storageMedium" label="Storage Solution" slug="medium" required />
                    </Col>
                    {formValues.storageMedium === "Other" && (
                        <Col sm={4} className="mb-3">
                            <Input accessor="storageMediumOther" label="Storage Solution Other" />
                        </Col>
                    )}
                    <Col sm={4} className="mb-3">
                        <Input accessor="mediumLotNumber" label="Storage Solution Lot Number" required />
                    </Col>
                    <Col sm={4} className="mb-3">
                        <DateInput label="Storage Solution Expiration Date" accessor="mediumExpirationDate" required />
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col className="mb-3">
                        <Input accessor="tissueFee" label="Tissue Fee" type="number" isCurrency min={0} />
                    </Col>
                    <Col className="mb-3">
                        <Input accessor="shippingFee" label="Shipping Fee" type="number" isCurrency min={0} />
                    </Col>
                    <Col className="mb-3">
                        <Form.Group>
                            <Form.Label>Total</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control readOnly value={total} />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                    <Col className="mb-3">
                        <Input accessor="poNumber" label="Purchase Order Number" />
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col>
                        <Checkbox
                            className="mb-3"
                            accessor="isShippedDirect"
                            label="This tissue is being shipped directly to surgery site"
                        />
                    </Col>
                    <Col>
                        <Select
                            accessor="importAccountId"
                            label="Import Location"
                            options={internalAccountOptions}
                            required={!formValues.isShippedDirect}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ControlledDateInput
                            label="Surgery Date"
                            value={surgeryDate}
                            onChange={(val) => setSurgeryDate(val?.toISOString())}
                        />
                    </Col>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Surgeon</Form.Label>
                            <ReactSelect
                                options={surgeonOptions}
                                value={surgeonOptions.find((s) => s.value === surgeonId)}
                                isDisabled={!surgeryDate}
                                onChange={(val) => setSurgeonId(Number(val?.value))}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minWidth: "200px",
                                    }),
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Select
                            accessor="surgeryId"
                            label="Surgery"
                            options={surgeryOptions}
                            isDisabled={!surgeryDate}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col className="d-flex justify-content-end">
                        <Button type="submit" disabled={!isDirty || isSubmitting}>
                            Save
                        </Button>
                    </Col>
                </Row>
            </StatefulForm>
        </Container>
    );
};
